import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Row, Col, Container, CustomInput, Table } from 'reactstrap';
import { Accordion } from 'cd-eft/components/common/Accordion';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { ButtonBlock } from '../../common/ButtonBlock/ButtonBlock';
import { FixtureModalVM } from './FixtureModalVM';
import { IUgoVertexParamsDTO } from 'cd-eft/models/Stores/UGODesignStore';
import { InputsElement } from 'cd-eft/components/Design/common/InputsElement';
import Draggable from 'react-draggable';
import './FixtureModal.scss';
import { verticalOutletSectionConfig } from './FixtureModal.labels.config';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';

interface IFixtureModalProps {
  vm: FixtureModalVM;
  onApply: (station: IUgoVertexParamsDTO) => void;
  onClose: () => void;
}

const FixtureModalComponent = ({ vm, onApply, onClose }: IFixtureModalProps) => {
  const {
    FixtureMainSection,
  } = vm.fixtureModalConfig;

  useEffect(() => {
    vm.init();
  }, [vm]);

  return (
    <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
      <div className={`ugo-fixture-modal ${Number(vm.inputForms.FixtureMainSection.fixtureType) === 6 ? 'riser-inlet-modal' : ''} ${Number(vm.inputForms.FixtureMainSection.fixtureType) === 8 ? 'vertical-outlet-modal' : ''}`}>
        <div id='tcs-header'>
          <h1 className="h5 mt-1 mb-1 ml-1">
            {`${vm.mode === 'edit'
              ? 'Edit'
              : 'Add'} 
          ${!!vm.selectedFixture
                ? vm.selectedFixture
                : 'Fixture'}`}
          </h1>
          <button
            className="tcs-close"
            aria-label="close tool"
            onClick={() => onClose()}>
            <Icon path={mdiClose} title="Close" size={1} />
          </button>
        </div>
        <Container>
          {Number(vm.inputForms.FixtureMainSection.fixtureType) !== 6 && (
            <Accordion
              className={'fixture-main accordion-wrapper'}
              headers={[`${vm.accordionHeader}`]}
              disabled={vm.accordionDisabled}>
              <>
                <InputsElement
                  config={{
                    ...FixtureMainSection.fixtureType,
                    options: FixtureMainSection.fixtureType.options?.filter(option => option.value !== 9) || []
                  }}
                  value={vm.inputForms.FixtureMainSection.fixtureType}
                  onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                  onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                  red={vm.redHighlighting.FixtureMainSection.fixtureType}
                />
                <div className='row rowTwo'>
                  {Object.entries(FixtureMainSection)
                    .slice(1, 5)
                    .map(([key, config]) => (
                      <div className={'col ' + key} key={'main-input-' + key}>
                        <InputsElement
                          config={config}
                          value={vm.inputForms['FixtureMainSection'][key]}
                          onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                          onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                          red={vm.redHighlighting['FixtureMainSection'][key]}
                        />
                      </div>
                    ))}
                </div>
                <Row>
                  {Object.entries(FixtureMainSection)
                    .slice(5, 8)
                    .map(([key, config]) => (
                      <div className={'col ' + key} key={'main-input-' + key}>
                        <InputsElement
                          config={config}
                          value={vm.inputForms['FixtureMainSection'][key]}
                          onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                          onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                          red={vm.redHighlighting['FixtureMainSection'][key]}
                        />
                      </div>
                    ))}
                </Row>
                <InputsElement
                  config={FixtureMainSection.manningN}
                  value={Number(vm.inputForms.FixtureMainSection.manningN) === 0 ? '0' : Number(vm.inputForms.FixtureMainSection.manningN).toFixed(4)}
                  onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                  onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                  red={vm.redHighlighting.FixtureMainSection.manningN}
                />
              </>
            </Accordion>
          )}
          {Number(vm.inputForms.FixtureMainSection.fixtureType) === 4 && <JunctionComponent vm={vm} />}
          {Number(vm.inputForms.FixtureMainSection.fixtureType) === 5 && <CanopyInletComponent vm={vm} />}
          {Number(vm.inputForms.FixtureMainSection.fixtureType) === 7 && <StandardOutletComponent vm={vm} />}
          {Number(vm.inputForms.FixtureMainSection.fixtureType) === 8 && <VerticalOutletComponent vm={vm} />}
          {Number(vm.inputForms.FixtureMainSection.fixtureType) === 6 && (
            <>
              <RiserInletComponent vm={vm} />
              {vm.showCalculatedResults && <RiserInletResultsComponent vm={vm} />}
            </>
          )}
          {vm.loading && <Loader message={vm.loadingMessage} />}
        </Container>
        <ButtonBlock
          className='action-buttons'
          buttons={[
            { title: 'Close', disabled: false, onClick: () => onClose(), className: 'usa-button--outline' },
            ...(Number(vm.inputForms.FixtureMainSection.fixtureType) === 6 ? [{ title: 'Calculate Inlet Q', disabled: false, onClick: () => vm.calculateInletQ() }] : []),
            { title: 'Apply', disabled: vm.applyDisabled, onClick: () => onApply(vm.getUpdatedStation()) },
          ]} />
      </div>
    </Draggable>
  );
};

export const StandardOutletComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  const {
    StandardOutletSection,
  } = vm.fixtureModalConfig;

  return (
    <Accordion
      className='accordion-wrapper'
      headers={['Standard Outlet']}
      disabled={false}>
      <>
        <h3 className='subheader'>Outlet Options</h3>
        <Row style={{ display: 'flex', alignItems: 'center' }}>
          {Object.entries(StandardOutletSection).map(([key, config]) => {
            // Show useAnimalGuard always, show length only if useAnimalGuard is checked
            if (key === 'head') {
              return null; // Skip head here, we'll handle it separately
            }
            if (key === 'length' && !vm.inputForms.StandardOutletSection.useAnimalGuard) {
              return null;
            }

            return (
              <div className={'col ' + key} key={'main-input-' + key}>
                <InputsElement
                  config={config}
                  value={vm.inputForms['StandardOutletSection'][key]}
                  onChange={(e) => vm.handleInputsOnChange(e, 'StandardOutletSection')}
                  onBlur={(e) => vm.handleInputsOnBlur(e, 'StandardOutletSection')}
                  red={vm.redHighlighting['StandardOutletSection'][key]}
                />
              </div>
            );
          })}
          {vm.inputForms.StandardOutletSection.useAnimalGuard && (
            <div className="col" style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '10px' }}>Head (ft):</span>
              <input
                type="number"
                step="0.01"
                className={`usa-input ${vm.redHighlighting.StandardOutletSection.head ? 'usa-input--error' : ''}`}
                style={{ width: '80px' }}
                value={vm.inputForms.StandardOutletSection.head}
                onChange={(e) => {
                  vm.handleInputsOnChange(e, 'StandardOutletSection');
                  vm.validateField('head', e.target.value, 'StandardOutletSection', true);
                }}
                onBlur={(e) => {
                  vm.handleInputsOnBlur(e, 'StandardOutletSection');
                  vm.validateField('head', e.target.value, 'StandardOutletSection', false);
                }}
                name="head"
              />
            </div>
          )}
        </Row>
      </>
    </Accordion>
  );
});


export const JunctionComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  const {
    JunctionSection,
  } = vm.fixtureModalConfig;

  return (
    <Accordion
      className='accordion-wrapper junction-accordion'
      headers={['Junction']}>
      <Row>
        <Col>
          <h3 className='subheader'>{JunctionSection.junctionFlowConnection.label}</h3>
          <div className="junction-override-toggle-group">
            <label
              htmlFor="overrideAddedQToggle"
              className={
                vm.inputForms.JunctionSection.override
                  ? 'toggle-label text-primary underline'
                  : 'toggle-label text-dark'
              }
            >
              Override Added Q
            </label>
            <CustomInput
              id="overrideAddedQToggle"
              name="overrideAddedQToggle"
              type="switch"
              bsSize="lg"
              disabled={false}
              checked={vm.inputForms.JunctionSection.override}
              onChange={() => vm.toggleOverrideAddedQ()}
              className={
                'checkbox-input-switch' +
                (vm.inputForms.JunctionSection.override
                  ? ' selected'
                  : '')
              }
            />
          </div>
          {Object.entries(JunctionSection)
            .slice(1, 3)
            .map(([key, config]) => (
              <InputsElement
                key={'junction-input' + key}
                config={{
                  ...config,
                  disabled: !vm.inputForms.JunctionSection.override
                }}
                value={vm.inputForms['JunctionSection'][key]}
                onChange={(e) => vm.handleInputsOnChange(e, 'JunctionSection')}
                onBlur={(e) => vm.handleInputsOnBlur(e, 'JunctionSection')}
                red={vm.redHighlighting['JunctionSection'][key]}
              />
            ))}
        </Col>
        <Col className='pipelineJunction'>
          <InputsElement
            config={JunctionSection.pipelineJunction}
            value={vm.inputForms.JunctionSection.pipelineJunction}
            onChange={(e) => vm.handleInputsOnChange(e, 'JunctionSection')}
            onBlur={(e) => vm.handleInputsOnBlur(e, 'JunctionSection')}
            red={vm.redHighlighting.JunctionSection.pipelineJunction}
          />
        </Col>
      </Row>
    </Accordion>
  );
});

export const VerticalOutletComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  const { VerticalOutletSection } = vm.fixtureModalConfig;
  return (
    <Accordion className={'vertical-outlet accordion-wrapper'} headers={[`Vertical Outlet`]} expanded={true}>
      <>
        {verticalOutletSectionConfig.map((row: any, i: number) => (
          <Row key={'vertical-outlet-row-' + i} className={'vertical-outlet-row-' + i}>
            {Array.from(row).map((col: any, j: number) => {
              // Skip the perforated field - it's been removed from the UI
              if (col.name === 'perforated') {
                return null;
              }

              // Conditionally disable fields based on useDrainPipe
              const isDrainPipeField = ['material', 'pipeSize', 'manningsN', 'pressurizable'].includes(col.name);
              const isRockQuantityField = col.name === 'rockQuantity';

              let fieldDisabled = false;
              if (isDrainPipeField) {
                fieldDisabled = !vm.inputForms.VerticalOutletSection.useDrainPipe;
              } else if (isRockQuantityField) {
                fieldDisabled = !vm.inputForms.VerticalOutletSection.useRockFill;
              }

              let value = vm.inputForms['VerticalOutletSection'][col.name];
              if (col.name === 'manningsN') {
                value = Number(vm.inputForms['VerticalOutletSection'][col.name]) === 0
                  ? '0'
                  : Number(vm.inputForms['VerticalOutletSection'][col.name]).toFixed(4);
              }

              return (
                <Col className={'vertical-outlet-col-' + j} key={'vertical-outlet-col-' + i + j}>
                  <InputsElement
                    config={{
                      ...VerticalOutletSection[col.name],
                      disabled: (fieldDisabled || VerticalOutletSection[col.name].disabled)
                    }}
                    value={value}
                    onChange={(e) => vm.handleInputsOnChange(e, 'VerticalOutletSection')}
                    onBlur={(e) => vm.handleInputsOnBlur(e, 'VerticalOutletSection')}
                    red={vm.redHighlighting['VerticalOutletSection'][col.name]}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </>
    </Accordion>
  );
});

export const CanopyInletComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  const {
    CanopyInletSection,
    WaterSourceSection
  } = vm.fixtureModalConfig;

  return (
    <Accordion
      className='accordion-wrapper'
      headers={['Canopy Inlet']}
      disabled={false}>
      <>
        <h3 className='subheader'>Inlet Options</h3>
        <Row style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
          {Object.entries(CanopyInletSection).slice(0, 2).map(([key, config]) => (
            <div className={'col ' + key} key={'main-input-' + key}>
              <InputsElement
                config={config}
                value={vm.inputForms['CanopyInletSection'][key]}
                onChange={(e) => vm.handleInputsOnChange(e, 'CanopyInletSection')}
                onBlur={(e) => vm.handleInputsOnBlur(e, 'CanopyInletSection')}
                red={vm.redHighlighting['CanopyInletSection'][key]}
              />
            </div>
          ))}
        </Row>
        <h3 className='subheader'>Water Source</h3>
        <Row>
          <Col className='narrow-width'>
            <strong>Terrace Condition</strong>
            <div className='d-flex'>
              <div>
                <div>Required Q (cfs):</div>
                <div>Flood Elevation (ft):</div>
                <div>Channel Elevation (ft):</div>
              </div>
              <div style={{ paddingLeft: '1rem' }}>
                <div>{Number(vm.inputForms.CanopyInletSection.terraceRequiredQ.toFixed(3))}</div>
                <div>{Number(vm.inputForms.CanopyInletSection.terraceFloodElev.toFixed(3))}</div>
                <div>{Number(vm.inputForms.CanopyInletSection.channelElev.toFixed(3))}</div>
              </div>
            </div>
          </Col>
          <Col>
            {Object.entries(WaterSourceSection).slice(0, 1).map(([key, config]) => (
              <div className={'col ' + key} key={'main-input-' + key}>
                <InputsElement
                  config={config}
                  value={vm.inputForms['CanopyInletSection'][key]}
                  onChange={(e) => vm.handleInputsOnChange(e, 'CanopyInletSection')}
                  onBlur={(e) => vm.handleInputsOnBlur(e, 'CanopyInletSection')}
                  red={vm.redHighlighting['CanopyInletSection'][key]}
                />
              </div>
            ))}
          </Col>
        </Row>
      </>
    </Accordion>
  );
});


export const RiserInletComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  const {
    FixtureMainSection,
    RiserInletSection,
    PerforatedRiserSection,
    WaterSourceSection,
    OrificeOffsetSection,
  } = vm.fixtureModalConfig;

  return (
    <>
      {/* Riser Inlet Accordion */}
      <Accordion
        className='accordion-wrapper'
        headers={['Riser Inlet']}
        disabled={false}>
        <>
          <Row>
            <div className='col fixtureType'>
              <InputsElement
                config={{
                  ...FixtureMainSection.fixtureType,
                  options: FixtureMainSection.fixtureType.options?.filter(option => option.value !== 9) || []
                }}
                value={vm.inputForms.FixtureMainSection.fixtureType}
                onChange={(e: any) => {
                  vm.handleInputsOnChange(e, 'FixtureMainSection');
                }}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.fixtureType}
              />
            </div>
          </Row>

          <Row style={{ margin: '0.5rem auto 0.5rem 0' }}>
            <div className='col-3 name px-0'>
              <InputsElement
                config={FixtureMainSection.name}
                value={vm.inputForms.FixtureMainSection.name}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.name}
              />
            </div>
            <div className='col-3 station'>
              <InputsElement
                config={FixtureMainSection.station}
                value={vm.inputForms.FixtureMainSection.station}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.station}
              />
            </div>
            <div className='col-3 pipeElevation'>
              <InputsElement
                config={FixtureMainSection.pipeElevation}
                value={vm.inputForms.FixtureMainSection.pipeElevation}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.pipeElevation}
              />
            </div>
            <div className='col-3 useManufactureRiser' style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <label className="usa-label" style={{ marginBottom: '1rem' }}>Use Manufactured Riser</label>
              <div className="usa-switch usa-switch--small">
                <input
                  className="usa-switch__input"
                  id="useManufactureRiser"
                  type="checkbox"
                  name="useManufactureRiser"
                  checked={vm.inputForms.RiserInletSection.useManufactureRiser}
                  onChange={async (e: any) => {
                    await vm.handleInputsOnChange(e, 'RiserInletSection');
                    vm.setSelectedManufacturedRiser(vm.selectedManufacturedRiser);
                  }}
                />
                <label className="usa-switch__label" htmlFor="useManufactureRiser"></label>
              </div>
            </div>
          </Row>

          {/* Manufactured Riser Table */}
          {vm.inputForms.RiserInletSection.useManufactureRiser && (
            <div className='manufactured-riser-table'>
              <div className='template-table-scrollable-wrapper'>
                <Table className="set-table usa-table--borderless">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Type</th>
                      <th>Diameter<br /> (in)</th>
                      <th>Material</th>
                      <th>Perforated Size<br /> (in)</th>
                      <th>Holes<br /> (ft)</th>
                      <th>Height<br /> (ft)</th>
                      <th>Guard</th>
                      <th>Top Opening<br /> (in)</th>
                      <th>Entrance Loss Coefficient (ke)</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {vm.manufacturedRiserOptions.map((option, index) => (
                      <tr key={'manufactured-riser-' + index} className={vm.selectedManufacturedRiser === index ? 'selected-row' : ''}>
                        <td>
                          <div className="usa-radio">
                            <input
                              className="usa-radio__input"
                              id={`manufactured-riser-${index}`}
                              type="radio"
                              name="selectedManufacturedRiser"
                              checked={vm.selectedManufacturedRiser === index}
                              onChange={() => vm.setSelectedManufacturedRiser(index)}
                            />
                            <label className="usa-radio__label" htmlFor={`manufactured-riser-${index}`}>
                              <span className='hide-this-label'>Iowa-1</span>
                            </label>
                          </div>
                        </td>
                        <td>{option.riserTypeName}</td>
                        <td>{option.diameterIn}</td>
                        <td>{option.material}</td>
                        <td>{option.holeDiameterIn ? option.holeDiameterIn : `${option.holeWidthIn} x ${option.heightIn}`}</td>
                        <td>{option.holesPerFoot}</td>
                        <td>{option.heightOfPerforationsFt}</td>
                        <td>{option.guard}</td>
                        <td>{option.topOpeningDiameterIn}</td>
                        <td>{option.ke}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          <Row>
            <div className='col-4 material px-2'>
              <InputsElement
                config={FixtureMainSection.material}
                value={vm.inputForms.FixtureMainSection.material}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.material}
              />
            </div>
            <div className='col-3 pipeSize'>
              <InputsElement
                config={FixtureMainSection.pipeSize}
                value={vm.inputForms.FixtureMainSection.pipeSize}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.pipeSize}
              />
            </div>
            <Col className='pressurizableMain'>
              <InputsElement
                config={FixtureMainSection.pressurizableMain}
                value={vm.inputForms.FixtureMainSection.pressurizableMain}
                onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
                onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
                red={vm.redHighlighting.FixtureMainSection.pressurizableMain}
              />
            </Col>
          </Row>

          <InputsElement
            config={FixtureMainSection.manningN}
            value={Number(vm.inputForms.FixtureMainSection.manningN) === 0 ? '0' : Number(vm.inputForms.FixtureMainSection.manningN).toFixed(4)}
            onChange={(e) => vm.handleInputsOnChange(e, 'FixtureMainSection')}
            onBlur={(e) => vm.handleInputsOnBlur(e, 'FixtureMainSection')}
            red={vm.redHighlighting.FixtureMainSection.manningN}
          />

          <div className='fixture-subheader'>Inlet Top</div>
          <Row style={{ display: 'flex', alignItems: 'center' }}>
            <div className='col-4 guard px-2'>
              <InputsElement
                config={RiserInletSection.guard}
                value={vm.inputForms.RiserInletSection.guard}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'RiserInletSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'RiserInletSection')}
                red={vm.redHighlighting.RiserInletSection.guard}
              />
            </div>
            <div className='col-3 topOpeningDiameter'>
              <InputsElement
                config={RiserInletSection.topOpeningDiameter}
                value={vm.inputForms.RiserInletSection.topOpeningDiameter}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'RiserInletSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'RiserInletSection')}
                red={vm.redHighlighting.RiserInletSection.topOpeningDiameter}
              />
            </div>
            <div className='col-3'>
              <InputsElement
                config={RiserInletSection.pluggedFraction}
                value={vm.inputForms.RiserInletSection.pluggedFraction}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'RiserInletSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'RiserInletSection')}
                red={vm.redHighlighting.RiserInletSection.pluggedFraction}
              />
            </div>
            <Col>
              <label className="usa-label">
                Qtop (cfs) = {vm.inputForms.RiserInletSection.qtop}
              </label>
            </Col>
          </Row>
        </>
      </Accordion>

      {/* Perforated Riser Accordion */}
      <Accordion
        className='accordion-wrapper'
        headers={['Perforated Riser']}
        disabled={false}>
        <>
          <Row>
            <div className='col holesPerFt'>
              <InputsElement
                config={PerforatedRiserSection.holesPerFt}
                value={vm.inputForms.PerforatedRiserSection.holesPerFt}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                red={vm.redHighlighting.PerforatedRiserSection.holesPerFt}
              />
            </div>
            <div className='col height'>
              <InputsElement
                config={PerforatedRiserSection.height}
                value={vm.inputForms.PerforatedRiserSection.height}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                red={vm.redHighlighting.PerforatedRiserSection.height}
              />
            </div>
            <div className='col pluggedPerfFraction'>
              <InputsElement
                config={PerforatedRiserSection.pluggedPerfFraction}
                value={vm.inputForms.PerforatedRiserSection.pluggedPerfFraction}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                red={vm.redHighlighting.PerforatedRiserSection.pluggedPerfFraction}
              />
            </div>
          </Row>

          <Row>
            <Col>
              <div className="radio-button-group py-2">
                <div className="radio-option">
                  <input
                    className="usa-radio__input"
                    id="roundPerf"
                    type="radio"
                    name="perfShape"
                    value="Round Perf"
                    checked={vm.inputForms.PerforatedRiserSection.perfShape === 'Round Perf'}
                    onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                  />
                  <label className="usa-radio__label" htmlFor="roundPerf">Round Perf</label>
                </div>
                <div className="radio-option">
                  <input
                    className="usa-radio__input"
                    id="rectangularPerf"
                    type="radio"
                    name="perfShape"
                    value="Rectangular Perf"
                    checked={vm.inputForms.PerforatedRiserSection.perfShape === 'Rectangular Perf'}
                    onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                  />
                  <label className="usa-radio__label" htmlFor="rectangularPerf">Rectangular Perf</label>
                </div>
              </div>
            </Col>
          </Row>

          <Row style={{ display: 'flex', alignItems: 'center' }}>
            {vm.inputForms.PerforatedRiserSection.perfShape === 'Round Perf' ? (
              // Round Perf input
              <div className='col-4 perfDiameter px-2'>
                <InputsElement
                  config={PerforatedRiserSection.perfDiameter}
                  value={vm.inputForms.PerforatedRiserSection.perfDiameter}
                  onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                  onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                  red={vm.redHighlighting.PerforatedRiserSection.perfDiameter}
                />
              </div>
            ) : (
              // Rectangular Perf inputs
              <>
                <div className='col-2 perfWidth px-2'>
                  <InputsElement
                    config={PerforatedRiserSection.perfWidth}
                    value={vm.inputForms.PerforatedRiserSection.perfWidth}
                    onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                    onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                    red={vm.redHighlighting.PerforatedRiserSection.perfWidth}
                  />
                </div>
                <div className='col-2 perfHeight px-2'>
                  <InputsElement
                    config={PerforatedRiserSection.perfHeight}
                    value={vm.inputForms.PerforatedRiserSection.perfHeight}
                    onChange={(e: any) => vm.handleInputsOnChange(e, 'PerforatedRiserSection')}
                    onBlur={(e: any) => vm.handleInputsOnBlur(e, 'PerforatedRiserSection')}
                    red={vm.redHighlighting.PerforatedRiserSection.perfHeight}
                  />
                </div>
              </>
            )}

            <div className='col-4' style={{ display: 'flex', alignItems: 'center', paddingTop: '1rem' }}>
              <label className="usa-label">
                Qperf (cfs) = {vm.inputForms.PerforatedRiserSection.qperf}
              </label>
            </div>

            <div className='col-2' style={{ marginRight: '3rem', paddingTop: '1rem' }}>
              <button className="usa-button" type="button">
                Calculate
              </button>
            </div>
          </Row>
        </>
      </Accordion>

      {/* Water Source Accordion */}
      <Accordion
        className='accordion-wrapper'
        headers={['Water Source']}
        disabled={false}>
        <>
          <Row>
            <Col className='narrow-width'>
              <p style={{ fontWeight: 'bold' }}>Terrace Condition</p>
              <div style={{ display: 'flex' }}>
                <div className='flex-column compact-spacing'>
                  <p>Required Q (cfs):</p>
                  <p>Flood Elevation (ft):</p>
                  <p>Channel Elevation (ft):</p>
                </div>
                <div className='flex-column compact-spacing' style={{ paddingLeft: '1rem' }}>
                  <p>{Number(vm.inputForms.WaterSourceSection.terraceRequiredQ.toFixed(3))}</p>
                  <p>{Number(vm.inputForms.WaterSourceSection.terraceFloodElev.toFixed(3))}</p>
                  <p>{Number(vm.inputForms.WaterSourceSection.channelElev.toFixed(3))}</p>
                </div>
              </div>
            </Col>
            <Col>
              {Object.entries(WaterSourceSection).slice(0, 1).map(([key, config]) => (
                <div className={'col ' + key} key={'main-input-' + key}>
                  <InputsElement
                    config={config}
                    value={vm.inputForms['WaterSourceSection'][key]}
                    onChange={(e) => vm.handleInputsOnChange(e, 'WaterSourceSection')}
                    onBlur={(e) => vm.handleInputsOnBlur(e, 'WaterSourceSection')}
                    red={vm.redHighlighting['WaterSourceSection'][key]}
                  />
                </div>
              ))}
            </Col>
          </Row>

          <Row style={{ display: 'flex', alignItems: 'center', padding: '1rem 0' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '1rem' }}>Average Flood Depth Factor</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                value={vm.inputForms.WaterSourceSection.avgFloodDepthFactor}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'WaterSourceSection')}
                name="avgFloodDepthFactor"
              />
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '1rem' }}>UGO Sizing Flood Depth Factor</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '120px' }}
                value={vm.inputForms.WaterSourceSection.ugoSizingFloodDepthFactor}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'WaterSourceSection')}
                name="ugoSizingFloodDepthFactor"
              />
            </Col>
          </Row>
        </>
      </Accordion>

      {/* Orifice Plate & Offset Pipe Accordion */}
      <Accordion
        className='accordion-wrapper'
        headers={['Orifice Plate & Offset Pipe']}
        disabled={false}>
        <>
          <Row style={{ display: 'flex', alignItems: 'center' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <div className="checkbox-inline">
                <input
                  type="checkbox"
                  id="useOrifice"
                  name="useOrifice"
                  checked={vm.inputForms.OrificeOffsetSection.useOrifice}
                  onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                  className="usa-checkbox__input"
                />
                <label htmlFor="useOrifice" className="usa-checkbox__label">
                  Use Orifice
                </label>
              </div>
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center', marginRight: '14rem' }}>
              <span className={`${!vm.inputForms.OrificeOffsetSection.useOrifice ? 'text-disabled' : ''}`} style={{ marginRight: '1rem' }}>Orifice Depth (in)</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                disabled={!vm.inputForms.OrificeOffsetSection.useOrifice}
                value={vm.inputForms.OrificeOffsetSection.orificeDepth}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                name="orificeDepth"
              />
            </Col>
          </Row>

          <Row style={{ display: 'flex', alignItems: 'center', padding: '1rem 0' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <div className="checkbox-inline">
                <input
                  type="checkbox"
                  id="useDefinedSize"
                  name="useDefinedSize"
                  disabled={!vm.inputForms.OrificeOffsetSection.useOrifice}
                  checked={vm.inputForms.OrificeOffsetSection.useDefinedSize}
                  onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                  className="usa-checkbox__input"
                />
                <label htmlFor="useDefinedSize" className="usa-checkbox__label">
                  Use Defined Size
                </label>
              </div>
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center', marginLeft: '3.5rem' }}>
              <span className={`${!vm.inputForms.OrificeOffsetSection.useDefinedSize ? 'text-disabled' : ''}`} style={{ marginRight: '1rem' }}>Diameter (in)</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                disabled={!vm.inputForms.OrificeOffsetSection.useDefinedSize}
                value={vm.inputForms.OrificeOffsetSection.diameter}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                name="diameter"
              />
            </Col>
            <Col>
              <span>{!vm.inputForms.OrificeOffsetSection.useOrifice ? 'No Orifice Plate' : ''}</span>
            </Col>
          </Row>

          <div className='fixture-subheader'>Offset Pipe (Optional)</div>

          <Row style={{ display: 'flex', alignItems: 'center' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <div className="junction-override-toggle-group">
                <label
                  htmlFor="useOffset"
                  className={
                    vm.inputForms.OrificeOffsetSection.useOffset
                      ? 'toggle-label text-primary underline'
                      : 'toggle-label text-dark'
                  }
                >
                  Use Offset
                </label>
                <div className="usa-switch usa-switch--small">
                  <input
                    className="usa-switch__input"
                    id="useOffset"
                    type="checkbox"
                    name="useOffset"
                    checked={vm.inputForms.OrificeOffsetSection.useOffset}
                    onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                  />
                  <label className="usa-switch__label" htmlFor="useOffset"></label>
                </div>
              </div>
            </Col>
          </Row>

          <Row style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <span className={`${!vm.inputForms.OrificeOffsetSection.useOffset ? 'text-disabled' : ''}`} style={{ marginRight: '1rem' }}>Offset Length (ft)</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                disabled={!vm.inputForms.OrificeOffsetSection.useOffset}
                value={vm.inputForms.OrificeOffsetSection.offsetLength}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                name="offsetLength"
              />
            </Col>
            <Col style={{ display: 'flex', alignItems: 'center', marginRight: '12rem' }}>
              <span className={`${!vm.inputForms.OrificeOffsetSection.useOffset ? 'text-disabled' : ''}`} style={{ marginRight: '1rem' }}>Elbow Elevation (ft)</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                disabled={!vm.inputForms.OrificeOffsetSection.useOffset}
                value={vm.inputForms.OrificeOffsetSection.elbowElevation}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                name="elbowElevation"
              />
            </Col>
          </Row>

          <Row style={{ display: 'flex', paddingBottom: '1rem' }}>
            <Col style={{ alignItems: 'center' }}>
              {Object.entries(OrificeOffsetSection)
                .slice(7, 8)
                .map(([key, config]) => (
                  <div className={`'col ' + key ${!vm.inputForms.OrificeOffsetSection.useOffset ? 'text-disabled' : ''}`} key={'main-input-' + key}>
                    <InputsElement
                      config={{ ...config, disabled: !vm.inputForms.OrificeOffsetSection.useOffset }}
                      value={vm.inputForms['OrificeOffsetSection'][key]}
                      onChange={(e) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                      onBlur={(e) => vm.handleInputsOnBlur(e, 'OrificeOffsetSection')}
                      red={vm.redHighlighting['OrificeOffsetSection'][key]}
                    />
                  </div>
                ))}
            </Col>
            <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: '100px' }} className={`${!vm.inputForms.OrificeOffsetSection.useOffset ? 'text-disabled' : ''}`}>
                <InputsElement
                  config={{ ...OrificeOffsetSection.offsetPipeSize, disabled: !vm.inputForms.OrificeOffsetSection.useOffset }}
                  value={vm.inputForms.OrificeOffsetSection.offsetPipeSize}
                  onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                  onBlur={(e) => vm.handleInputsOnBlur(e, 'OrificeOffsetSection')}
                  red={vm.redHighlighting.OrificeOffsetSection.offsetPipeSize}
                />
              </div>
            </Col>
            <Col style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', marginTop: '10px' }}>
              <div className="checkbox-inline">
                <input
                  type="checkbox"
                  id="pressurizable"
                  name="pressurizable"
                  disabled={!vm.inputForms.OrificeOffsetSection.useOffset || OrificeOffsetSection.pressurizable.disabled}
                  checked={vm.inputForms.OrificeOffsetSection.pressurizable}
                  onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                  className="usa-checkbox__input"
                />
                <label htmlFor="pressurizable" className="usa-checkbox__label">
                  Pressurizable
                </label>
              </div>
            </Col>
          </Row>

          <Row style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <span className={`${!vm.inputForms.OrificeOffsetSection.useOffset ? 'text-disabled' : ''}`} style={{ marginRight: '1rem' }}>Manning's N</span>
              <input
                type="number"
                className="usa-input"
                style={{ width: '100px' }}
                disabled={!vm.inputForms.OrificeOffsetSection.useOffset}
                value={Number(vm.inputForms.OrificeOffsetSection.offsetManningN) === 0 ? '0' : Number(vm.inputForms.OrificeOffsetSection.offsetManningN).toFixed(4)}
                onChange={(e: any) => vm.handleInputsOnChange(e, 'OrificeOffsetSection')}
                name="offsetManningN"
                readOnly={true}
              />
            </Col>
            <Col>
              <span>{!vm.inputForms.OrificeOffsetSection.useOffset ? 'No offset pipe' : ''}</span>
            </Col>
          </Row>
        </>
      </Accordion>
    </>
  );
});

export const RiserInletResultsComponent = observer(({ vm }: { vm: FixtureModalVM }) => {
  return (
    <Accordion
      className={`accordion-wrapper ${vm.showCalculatedResults ? 'blue-accordion' : ''}`}
      headers={['Result for Design Controls']}
      disabled={false}>
      <>
        <Row style={{ marginLeft: '-1rem' }}>
          <Col xs="3">
            <span>Average Flow (cfs):</span>
          </Col>
          <Col xs="4">
            <span>{Number(vm.inputForms.ResultsSection.averageFlow) === 0 ? '0' : Number(vm.inputForms.ResultsSection.averageFlow).toFixed(2)}</span>
          </Col>
          <Col xs="4">
            <span>Qperf + Qtop = {Number(vm.inputForms.ResultsSection.qperfPlusQtop) === 0 ? '0' : Number(vm.inputForms.ResultsSection.qperfPlusQtop).toFixed(2)}</span>
          </Col>
        </Row>

        <Row style={{ marginLeft: '-1rem' }}>
          <Col xs="3">
            <span>Release Time:</span>
          </Col>
          <Col xs="4">
            <span>{vm.inputForms.ResultsSection.releaseTime}</span>
          </Col>
          <Col xs="4">
            {/* Empty column for alignment */}
          </Col>
        </Row>

        <Row style={{ marginLeft: '-1rem', paddingBottom: '0.5rem' }}>
          <Col xs="3">
            <span>UGO Sizing Flow (cfs):</span>
          </Col>
          <Col xs="4">
            <span>{Number(vm.inputForms.ResultsSection.ugoSizingFlow) === 0 ? '0' : Number(vm.inputForms.ResultsSection.ugoSizingFlow).toFixed(2)}</span>
          </Col>
          <Col xs="4">
            <span>Qperf + Qtop = {Number(vm.inputForms.ResultsSection.qperfPlusQtopUgo) === 0 ? '0' : Number(vm.inputForms.ResultsSection.qperfPlusQtopUgo).toFixed(2)}</span>
          </Col>
        </Row>
      </>
    </Accordion>
  );
});

export const FixtureModal = observer(FixtureModalComponent);